import type { DeepPartial } from '@pba/utils';
import type { QueryKey } from '@tanstack/react-query';

export type GenericDetailQueryKey<FilterGeneric = never, ID = string> = {
  filter?: DeepPartial<FilterGeneric>;
  id?: ID;
};

export type GenericListQueryKey<FilterGeneric = never, SorterGeneric = never> = {
  filter?: DeepPartial<FilterGeneric>;
  pagination?: DeepPartial<Pagination>;
  sorter?: DeepPartial<SorterGeneric>;
};

export type GenericTotalQueryKey<ListQuery extends GenericListQueryKey<unknown, unknown>> = Pick<ListQuery, 'filter'>;

export type Pagination = {
  pageSize: number;
  // TODO maybe type that allow position only greater then `1` because that is reality
  position: number;
};

export const generateDetailQueryKey =
  <const TPrefix extends QueryKey>({ prefix }: { prefix: TPrefix }) =>
  <DetailQuery extends GenericDetailQueryKey<unknown, unknown>>(params: DetailQuery) =>
    [...prefix, 'detail', params] as const;

export const generateListQueryKey =
  <const TPrefix extends QueryKey>({ prefix }: { prefix: TPrefix }) =>
  <ListQuery extends GenericListQueryKey<unknown, unknown>>(params: ListQuery) =>
    [...prefix, 'list', params] as const;

export const generateTotalQueryKey =
  <ListQuery extends GenericListQueryKey<unknown, unknown>, const TListKey extends QueryKey>({
    generateListQueryKey: innerGenerateListQueryKey,
  }: {
    generateListQueryKey: (params: ListQuery) => TListKey;
  }) =>
  (params: GenericTotalQueryKey<ListQuery> & ListQuery) =>
    [...innerGenerateListQueryKey(params), 'total'] as const;

export const generateInfiniteQueryKey =
  <ListQuery extends GenericListQueryKey<unknown, unknown>, const TListKey extends QueryKey>({
    generateListQueryKey: innerGenerateListQueryKey,
  }: {
    generateListQueryKey: (params: ListQuery) => TListKey;
  }) =>
  (params: ListQuery) =>
    [...innerGenerateListQueryKey(params), 'infinite'] as const;
